import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { leadsService } from '../services/leads.service';
import { Lead } from '../types/lead.types';
import { Loading } from '../components/common/Loading';
import { PriorityBadge } from '../components/leads/PriorityBadge';
import { StatusChip } from '../components/leads/StatusChip';
import { LeadForm } from '../components/leads/LeadForm';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

export const LeadDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuthStore();
  const [lead, setLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Check org_role for permissions
  const canEdit = profile?.org_role !== 'viewer';
  const canDelete = profile?.org_role === 'org_admin';

  useEffect(() => {
    if (id) {
      loadLead(id);
    }
  }, [id]);

  const loadLead = async (leadId: string) => {
    try {
      setIsLoading(true);
      const data = await leadsService.getLeadById(leadId);
      setLead(data);
    } catch (error: any) {
      toast.error('Failed to load lead details');
      navigate('/leads');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (values: any) => {
    if (!lead) return;
    
    setIsSubmitting(true);
    try {
      console.log('Updating lead with values:', values);
      const updated = await leadsService.updateLead(lead.id, values);
      console.log('Lead updated successfully:', updated);
      setLead(updated);
      toast.success('Lead updated successfully');
      setShowEditForm(false);
    } catch (error: any) {
      console.error('Failed to update lead:', error);
      toast.error(error.response?.data?.message || 'Failed to update lead');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!lead) return;
    
    setIsDeleting(true);
    try {
      console.log('Attempting to delete lead:', lead.id);
      await leadsService.deleteLead(lead.id);
      console.log('Lead deleted successfully');
      toast.success('Lead deleted successfully');
      setShowDeleteDialog(false);
      navigate('/leads');
    } catch (error: any) {
      console.error('Delete lead error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to delete lead');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return <Loading message="Loading lead details..." />;
  }

  if (!lead) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Lead not found
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/leads')}
            sx={{ mt: 2 }}
          >
            Back to Leads
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" sx={{ alignItems: 'center' }} spacing={2}>
          <IconButton onClick={() => navigate('/leads')} size="large">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            Lead Details
          </Typography>
          <Stack direction="row" spacing={2}>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setShowEditForm(true)}
              >
                Edit Lead
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Main Information */}
        <Box sx={{ flex: '1 1 66%' }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              {lead.first_name} {lead.last_name}
            </Typography>
            
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <StatusChip status={lead.status} />
              <PriorityBadge priority={lead.priority} />
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Assigned To Section */}
            {lead.assigned_to && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Assigned To
                </Typography>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                    }}
                  >
                    {lead.assigned_user_name ? lead.assigned_user_name.charAt(0).toUpperCase() : 'U'}
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {lead.assigned_user_name || 'Unknown User'}
                    </Typography>
                    {lead.assigned_user_email && (
                      <Typography variant="body2" color="text.secondary">
                        {lead.assigned_user_email}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            )}

            {!lead.assigned_to && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Assigned To
                </Typography>
                <Chip label="Unassigned" size="small" color="default" />
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              {lead.email && (
                <Box>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <EmailIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body2">{lead.email}</Typography>
                    </Box>
                  </Stack>
                </Box>
              )}

              {lead.phone && (
                <Box>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <PhoneIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body2">{lead.phone}</Typography>
                    </Box>
                  </Stack>
                </Box>
              )}

              {lead.company && (
                <Box>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <BusinessIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Company
                      </Typography>
                      <Typography variant="body2">{lead.company}</Typography>
                    </Box>
                  </Stack>
                </Box>
              )}

              {lead.position && (
                <Box>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <BusinessIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Position
                      </Typography>
                      <Typography variant="body2">{lead.position}</Typography>
                    </Box>
                  </Stack>
                </Box>
              )}

              {lead.estimated_value && (
                <Box>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <AttachMoneyIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Estimated Value
                      </Typography>
                      <Typography variant="body2">
                        USD {lead.estimated_value.toLocaleString()}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}

              {lead.source && (
                <Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Source
                    </Typography>
                    <Typography variant="body2">{lead.source}</Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {lead.notes && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                    {lead.notes}
                  </Typography>
                </Box>
              </>
            )}

            {lead.tags && lead.tags.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Tags
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {lead.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" />
                    ))}
                  </Stack>
                </Box>
              </>
            )}
          </Paper>
        </Box>

        {/* Sidebar */}
        <Box sx={{ flex: '1 1 33%' }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Timeline
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                  </Stack>
                  <Typography variant="body2">
                    {formatDate(lead.created_at)}
                  </Typography>
                </Box>

                {lead.last_contacted_at && (
                  <Box>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Last Contacted
                      </Typography>
                    </Stack>
                    <Typography variant="body2">
                      {formatDate(lead.last_contacted_at)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <LeadForm
        open={showEditForm}
        onClose={() => setShowEditForm(false)}
        onSubmit={handleUpdate}
        initialValues={lead}
        title="Edit Lead"
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
      >
        <DialogTitle>Delete Lead?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this lead? This action cannot be undone.
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {lead?.first_name} {lead?.last_name}
              </Typography>
              {lead?.company && (
                <Typography variant="body2" color="text.secondary">
                  {lead.company}
                </Typography>
              )}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} color="inherit" disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={isDeleting} autoFocus>
            {isDeleting ? 'Deleting...' : 'Delete Lead'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
